import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CenturionFooter from '@/components/CenturionFooter';
import {
  getSubscribers,
  addSubscriber,
  removeSubscriber,
  getSubscriberCount,
  sendBulkEmail,
  getEmailStatus,
  getMagazines,
  notifyPublication,
  getEmailCampaigns,
  getCampaignDetails,
  getEmailAnalyticsOverview,
  scheduleEmail,
  getScheduledEmails,
  cancelScheduledEmail,
  getQueueStats,
  bulkAddSubscribers,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Users,
  Mail,
  Plus,
  Trash2,
  Send,
  AlertCircle,
  CheckCircle,
  Loader2,
  BookOpen,
  UserPlus,
  MailOpen,
  BarChart3,
  Eye,
  TrendingUp,
  Clock,
  Target,
  CalendarClock,
  XCircle,
  Timer,
  Upload,
  FileText,
} from 'lucide-react';
import { format } from 'date-fns';

const SubscribersPage = () => {
  const navigate = useNavigate();
  
  const [subscribers, setSubscribers] = useState([]);
  const [magazines, setMagazines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  
  // Campaigns & Analytics
  const [campaigns, setCampaigns] = useState([]);
  const [emailAnalytics, setEmailAnalytics] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [isCampaignDetailOpen, setIsCampaignDetailOpen] = useState(false);
  const [campaignLoading, setCampaignLoading] = useState(false);
  
  // Add subscriber
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newSubscriber, setNewSubscriber] = useState({ name: '', email: '' });
  const [adding, setAdding] = useState(false);
  
  // Bulk email
  const [isBulkEmailOpen, setIsBulkEmailOpen] = useState(false);
  const [bulkEmail, setBulkEmail] = useState({ subject: '', html_content: '' });
  const [sending, setSending] = useState(false);
  
  // Notify publication
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const [selectedMagazine, setSelectedMagazine] = useState('');
  const [notifying, setNotifying] = useState(false);

  // Scheduled emails
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [queueStats, setQueueStats] = useState(null);

  // Bulk import
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);

  // Schedule mode for dialogs
  const [bulkScheduleMode, setBulkScheduleMode] = useState(false);
  const [bulkScheduleDate, setBulkScheduleDate] = useState('');
  const [notifyScheduleMode, setNotifyScheduleMode] = useState(false);
  const [notifyScheduleDate, setNotifyScheduleDate] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subsRes, countRes, statusRes, magsRes, campaignsRes, analyticsRes, scheduledRes, queueRes] = await Promise.all([
        getSubscribers(),
        getSubscriberCount(),
        getEmailStatus(),
        getMagazines(),
        getEmailCampaigns(),
        getEmailAnalyticsOverview(),
        getScheduledEmails(),
        getQueueStats(),
      ]);
      setSubscribers(subsRes.data);
      setSubscriberCount(countRes.data.count);
      setEmailConfigured(statusRes.data.configured);
      setMagazines(magsRes.data.filter(m => m.published));
      setCampaigns(campaignsRes.data);
      setEmailAnalytics(analyticsRes.data);
      setScheduledEmails(scheduledRes.data);
      setQueueStats(queueRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCampaign = async (campaignId) => {
    setCampaignLoading(true);
    setIsCampaignDetailOpen(true);
    try {
      const response = await getCampaignDetails(campaignId);
      setCampaignDetails(response.data);
    } catch (error) {
      toast.error('Failed to load campaign details');
    } finally {
      setCampaignLoading(false);
    }
  };

  const handleAddSubscriber = async () => {
    if (!newSubscriber.name.trim() || !newSubscriber.email.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setAdding(true);
    try {
      const response = await addSubscriber(newSubscriber);
      setSubscribers([response.data, ...subscribers]);
      setSubscriberCount(subscriberCount + 1);
      setNewSubscriber({ name: '', email: '' });
      setIsAddOpen(false);
      toast.success('Subscriber added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add subscriber');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveSubscriber = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    
    try {
      await removeSubscriber(id);
      setSubscribers(subscribers.filter(s => s.id !== id));
      setSubscriberCount(subscriberCount - 1);
      toast.success('Subscriber removed');
    } catch (error) {
      toast.error('Failed to remove subscriber');
    }
  };

  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) {
      toast.error('Please enter subscriber data');
      return;
    }
    setBulkImporting(true);
    try {
      const lines = bulkImportText.split('\n').map(l => l.trim()).filter(Boolean);
      const parsed = lines.map(line => {
        // Support: "email", "email,name", "name <email>", "name,email"
        const angleBracket = line.match(/^(.+?)\s*<(.+?)>$/);
        if (angleBracket) return { email: angleBracket[2].trim(), name: angleBracket[1].trim() };
        
        const parts = line.split(/[,\t;]+/).map(p => p.trim());
        if (parts.length >= 2) {
          // Determine which part is email
          if (parts[0].includes('@')) return { email: parts[0], name: parts[1] };
          return { email: parts[1], name: parts[0] };
        }
        return { email: parts[0], name: '' };
      });

      const res = await bulkAddSubscribers(parsed);
      const { added, skipped, reactivated } = res.data;
      toast.success(`Imported: ${added} added, ${reactivated} reactivated, ${skipped} skipped`);
      setIsBulkImportOpen(false);
      setBulkImportText('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import subscribers');
    } finally {
      setBulkImporting(false);
    }
  };

  const handleSendBulkEmail = async () => {
    if (!bulkEmail.subject.trim() || !bulkEmail.html_content.trim()) {
      toast.error('Please fill in subject and content');
      return;
    }

    if (bulkScheduleMode) {
      if (!bulkScheduleDate) {
        toast.error('Please select a date and time');
        return;
      }
      const scheduledTime = new Date(bulkScheduleDate);
      if (scheduledTime <= new Date()) {
        toast.error('Scheduled time must be in the future');
        return;
      }
      setSending(true);
      try {
        await scheduleEmail({
          email_type: 'bulk',
          subject: bulkEmail.subject,
          html_content: bulkEmail.html_content,
          scheduled_for: scheduledTime.toISOString(),
        });
        toast.success(`Email scheduled for ${scheduledTime.toLocaleString()}`);
        setIsBulkEmailOpen(false);
        setBulkEmail({ subject: '', html_content: '' });
        setBulkScheduleMode(false);
        setBulkScheduleDate('');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to schedule email');
      } finally {
        setSending(false);
      }
      return;
    }
    
    setSending(true);
    try {
      const response = await sendBulkEmail(bulkEmail);
      toast.success(`Email sent to ${response.data.sent_count} subscribers`);
      setIsBulkEmailOpen(false);
      setBulkEmail({ subject: '', html_content: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const handleNotifyPublication = async () => {
    if (!selectedMagazine) {
      toast.error('Please select a magazine');
      return;
    }

    if (notifyScheduleMode) {
      if (!notifyScheduleDate) {
        toast.error('Please select a date and time');
        return;
      }
      const scheduledTime = new Date(notifyScheduleDate);
      if (scheduledTime <= new Date()) {
        toast.error('Scheduled time must be in the future');
        return;
      }
      setNotifying(true);
      try {
        const mag = magazines.find(m => m.id === selectedMagazine);
        await scheduleEmail({
          email_type: 'publication_notification',
          subject: `New Magazine: ${mag?.title || 'New Edition'}`,
          magazine_id: selectedMagazine,
          scheduled_for: scheduledTime.toISOString(),
        });
        toast.success(`Notification scheduled for ${scheduledTime.toLocaleString()}`);
        setIsNotifyOpen(false);
        setSelectedMagazine('');
        setNotifyScheduleMode(false);
        setNotifyScheduleDate('');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to schedule notification');
      } finally {
        setNotifying(false);
      }
      return;
    }
    
    setNotifying(true);
    try {
      const response = await notifyPublication(selectedMagazine);
      toast.success(`Notification sent to ${response.data.sent_count} subscribers`);
      setIsNotifyOpen(false);
      setSelectedMagazine('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notifications');
    } finally {
      setNotifying(false);
    }
  };

  const handleCancelScheduled = async (id) => {
    if (!window.confirm('Cancel this scheduled email?')) return;
    try {
      await cancelScheduledEmail(id);
      setScheduledEmails(scheduledEmails.filter(e => e.id !== id));
      toast.success('Scheduled email cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="spinner" style={{ width: '48px', height: '48px' }} />
      </div>
    );
  }

  return (
    <div className="creative-bg">
      <div className="wave-decoration" />
      <div className="floating-shape floating-shape-1" />
      <div className="floating-shape floating-shape-2" />
      
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-to-dashboard"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#4A5568] hover:text-[#0A192F]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-[#FF6F61]" />
              <h1 className="font-serif text-2xl text-[#0A192F]">Subscribers</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              data-testid="bulk-import-btn"
              onClick={() => setIsBulkImportOpen(true)}
              variant="outline"
              className="border-[#E8E2D2] text-[#4A5568] hover:bg-[#F4F1EA]"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
            <Button
              data-testid="add-subscriber-btn"
              onClick={() => setIsAddOpen(true)}
              className="btn-coral"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Subscriber
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 relative z-10">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card-ambient p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-[#40E0D0]" />
              <span className="text-sm text-[#4A5568]">Subscribers</span>
            </div>
            <p className="text-3xl font-bold text-[#0A192F]">{subscriberCount}</p>
          </div>
          
          <div className="card-ambient p-6">
            <div className="flex items-center gap-3 mb-2">
              <Send className="w-5 h-5 text-[#FF6F61]" />
              <span className="text-sm text-[#4A5568]">Emails Sent</span>
            </div>
            <p className="text-3xl font-bold text-[#0A192F]">{emailAnalytics?.total_emails_sent || 0}</p>
          </div>
          
          <div className="card-ambient p-6">
            <div className="flex items-center gap-3 mb-2">
              <Eye className="w-5 h-5 text-[#40E0D0]" />
              <span className="text-sm text-[#4A5568]">Opens</span>
            </div>
            <p className="text-3xl font-bold text-[#0A192F]">{emailAnalytics?.total_emails_opened || 0}</p>
          </div>

          <div className="card-ambient p-6" data-testid="total-clicks-stat">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-[#FFB347]" />
              <span className="text-sm text-[#4A5568]">Clicks</span>
            </div>
            <p className="text-3xl font-bold text-[#0A192F]">{emailAnalytics?.total_emails_clicked || 0}</p>
          </div>
          
          <div className="card-ambient p-6">
            <div className="flex items-center gap-3 mb-2">
              <Target className="w-5 h-5 text-[#FF6F61]" />
              <span className="text-sm text-[#4A5568]">Open Rate</span>
            </div>
            <p className="text-3xl font-bold text-[#0A192F]">{emailAnalytics?.overall_open_rate || 0}%</p>
          </div>

          <div className="card-ambient p-6" data-testid="click-rate-stat">
            <div className="flex items-center gap-3 mb-2">
              <Target className="w-5 h-5 text-[#FFB347]" />
              <span className="text-sm text-[#4A5568]">Click Rate</span>
            </div>
            <p className="text-3xl font-bold text-[#0A192F]">{emailAnalytics?.overall_click_rate || 0}%</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="subscribers" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 bg-white border border-[#E8E2D2] rounded-xl p-1">
            <TabsTrigger value="subscribers" className="data-[state=active]:bg-[#F4F1EA] rounded-lg">
              <Users className="w-4 h-4 mr-2" />
              Subscribers
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="data-[state=active]:bg-[#F4F1EA] rounded-lg">
              <BarChart3 className="w-4 h-4 mr-2" />
              Campaigns
            </TabsTrigger>
            <TabsTrigger value="scheduled" data-testid="scheduled-tab" className="data-[state=active]:bg-[#F4F1EA] rounded-lg">
              <CalendarClock className="w-4 h-4 mr-2" />
              Scheduled
              {queueStats?.pending > 0 && (
                <span className="ml-2 text-xs bg-[#FF6F61] text-white rounded-full px-2 py-0.5">
                  {queueStats.pending}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers" className="space-y-6">
            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                data-testid="add-subscriber-btn-2"
                onClick={() => setIsAddOpen(true)}
                className="btn-coral"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Subscriber
              </Button>
              <Button
                data-testid="bulk-email-btn"
                onClick={() => setIsBulkEmailOpen(true)}
                disabled={subscriberCount === 0}
                variant="outline"
                className="border-[#E8E2D2] text-[#4A5568]"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Bulk Email
              </Button>
              <Button
                data-testid="notify-publication-btn"
                onClick={() => setIsNotifyOpen(true)}
                disabled={subscriberCount === 0 || magazines.length === 0}
                variant="outline"
                className="border-[#E8E2D2] text-[#4A5568]"
              >
                <MailOpen className="w-4 h-4 mr-2" />
                Notify Publication
              </Button>
            </div>

            {/* Subscribers List */}
            <div className="card-ambient">
              <div className="p-6 border-b border-[#E8E2D2] flex items-center justify-between">
                <h2 className="font-serif text-xl text-[#0A192F]">Subscriber List</h2>
                {!emailConfigured && (
                  <div className="flex items-center gap-2 text-sm text-yellow-600">
                    <AlertCircle className="w-4 h-4" />
                    Add RESEND_API_KEY to enable emails
                  </div>
                )}
              </div>
          
          {subscribers.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-[#E8E2D2] mx-auto mb-4" />
              <h3 className="font-serif text-xl text-[#0A192F] mb-2">No Subscribers Yet</h3>
              <p className="text-[#4A5568] mb-6">Start building your audience by adding subscribers.</p>
              <Button onClick={() => setIsAddOpen(true)} className="btn-coral">
                <Plus className="w-4 h-4 mr-2" />
                Add First Subscriber
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="divide-y divide-[#E8E2D2]">
                {subscribers.map((subscriber) => (
                  <div
                    key={subscriber.id}
                    data-testid={`subscriber-${subscriber.id}`}
                    className="p-4 flex items-center justify-between hover:bg-[#F4F1EA]/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#40E0D0] to-[#00CED1] flex items-center justify-center text-white font-medium">
                        {subscriber.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-[#0A192F]">{subscriber.name}</p>
                        <p className="text-sm text-[#4A5568]">{subscriber.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-[#4A5568]">
                        {format(new Date(subscriber.subscribed_at), 'MMM d, yyyy')}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveSubscriber(subscriber.id)}
                        className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
          </TabsContent>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns" className="space-y-6">
            {/* Campaign Stats */}
            {emailAnalytics?.best_campaign && (
              <div className="card-ambient p-6">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium text-[#4A5568]">Best Performing Campaign</span>
                </div>
                <p className="text-lg font-medium text-[#0A192F]">{emailAnalytics.best_campaign.name}</p>
                <p className="text-sm text-[#4A5568]">
                  {emailAnalytics.best_open_rate}% open rate • {emailAnalytics.best_campaign.total_sent} sent
                </p>
              </div>
            )}

            {/* Campaigns List */}
            <div className="card-ambient">
              <div className="p-6 border-b border-[#E8E2D2]">
                <h2 className="font-serif text-xl text-[#0A192F]">Campaign History</h2>
              </div>
              
              {campaigns.length === 0 ? (
                <div className="p-12 text-center">
                  <BarChart3 className="w-16 h-16 text-[#E8E2D2] mx-auto mb-4" />
                  <h3 className="font-serif text-xl text-[#0A192F] mb-2">No Campaigns Yet</h3>
                  <p className="text-[#4A5568]">Send your first email to start tracking analytics.</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="divide-y divide-[#E8E2D2]">
                    {campaigns.map((campaign) => {
                      const openRate = campaign.total_sent > 0 
                        ? Math.round((campaign.total_opened / campaign.total_sent) * 100) 
                        : 0;
                      return (
                        <div
                          key={campaign.id}
                          data-testid={`campaign-${campaign.id}`}
                          className="p-4 hover:bg-[#F4F1EA]/50 transition-colors cursor-pointer"
                          onClick={() => handleViewCampaign(campaign.id)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  campaign.campaign_type === 'publication_notification' 
                                    ? 'bg-[#40E0D0]/20 text-[#00897B]' 
                                    : 'bg-[#FF6F61]/20 text-[#FF6F61]'
                                }`}>
                                  {campaign.campaign_type === 'publication_notification' ? 'Publication' : 'Bulk Email'}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  campaign.status === 'completed' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {campaign.status}
                                </span>
                              </div>
                              <p className="font-medium text-[#0A192F]">{campaign.name}</p>
                              <p className="text-sm text-[#4A5568] truncate">{campaign.subject}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-[#0A192F]">{openRate}%</p>
                              <p className="text-xs text-[#4A5568]">open rate</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 mt-3 text-xs text-[#4A5568]">
                            <span className="flex items-center gap-1">
                              <Send className="w-3 h-3" />
                              {campaign.total_sent} sent
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {campaign.total_opened} opened
                            </span>
                            <span className="flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" />
                              {campaign.total_clicked || 0} clicked
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {format(new Date(campaign.created_at), 'MMM d, yyyy')}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>
          </TabsContent>

          {/* Scheduled Tab */}
          <TabsContent value="scheduled" className="space-y-6">
            {/* Queue Stats */}
            {queueStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { label: 'Pending', value: queueStats.pending, color: 'text-yellow-600', bg: 'bg-yellow-50' },
                  { label: 'Processing', value: queueStats.processing, color: 'text-blue-600', bg: 'bg-blue-50' },
                  { label: 'Completed', value: queueStats.completed, color: 'text-green-600', bg: 'bg-green-50' },
                  { label: 'Failed', value: queueStats.failed, color: 'text-red-600', bg: 'bg-red-50' },
                  { label: 'Cancelled', value: queueStats.cancelled, color: 'text-gray-600', bg: 'bg-gray-50' },
                ].map(stat => (
                  <div key={stat.label} className={`${stat.bg} rounded-xl p-4 text-center`}>
                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                    <p className="text-xs text-[#4A5568]">{stat.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Next Scheduled */}
            {queueStats?.next_scheduled && (
              <div className="card-ambient p-4 flex items-center gap-3">
                <Timer className="w-5 h-5 text-[#40E0D0]" />
                <div>
                  <p className="text-sm text-[#4A5568]">Next scheduled send</p>
                  <p className="font-medium text-[#0A192F]">
                    {queueStats.next_scheduled.subject} — {format(new Date(queueStats.next_scheduled.scheduled_for), 'MMM d, yyyy h:mm a')}
                  </p>
                </div>
              </div>
            )}

            {/* Scheduled Emails List */}
            <div className="card-ambient">
              <div className="p-6 border-b border-[#E8E2D2]">
                <h2 className="font-serif text-xl text-[#0A192F]">Scheduled Emails</h2>
              </div>
              
              {scheduledEmails.length === 0 ? (
                <div className="p-12 text-center">
                  <CalendarClock className="w-16 h-16 text-[#E8E2D2] mx-auto mb-4" />
                  <h3 className="font-serif text-xl text-[#0A192F] mb-2">No Scheduled Emails</h3>
                  <p className="text-[#4A5568]">Schedule emails from the Bulk Email or Notify Publication dialogs.</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="divide-y divide-[#E8E2D2]">
                    {scheduledEmails.map((email) => {
                      const statusColors = {
                        pending: 'bg-yellow-100 text-yellow-700',
                        processing: 'bg-blue-100 text-blue-700',
                        completed: 'bg-green-100 text-green-700',
                        failed: 'bg-red-100 text-red-700',
                        cancelled: 'bg-gray-100 text-gray-500',
                      };
                      return (
                        <div
                          key={email.id}
                          data-testid={`scheduled-email-${email.id}`}
                          className="p-4 hover:bg-[#F4F1EA]/50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  email.email_type === 'publication_notification'
                                    ? 'bg-[#40E0D0]/20 text-[#00897B]'
                                    : 'bg-[#FF6F61]/20 text-[#FF6F61]'
                                }`}>
                                  {email.email_type === 'publication_notification' ? 'Publication' : 'Bulk Email'}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[email.status] || 'bg-gray-100 text-gray-500'}`}>
                                  {email.status}
                                </span>
                              </div>
                              <p className="font-medium text-[#0A192F]">{email.subject}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-[#4A5568]">
                                <span className="flex items-center gap-1">
                                  <CalendarClock className="w-3 h-3" />
                                  Scheduled: {format(new Date(email.scheduled_for), 'MMM d, yyyy h:mm a')}
                                </span>
                                {email.processed_at && (
                                  <span className="flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    Processed: {format(new Date(email.processed_at), 'MMM d, yyyy h:mm a')}
                                  </span>
                                )}
                              </div>
                              {email.error_message && (
                                <p className="text-xs text-red-500 mt-1">{email.error_message}</p>
                              )}
                            </div>
                            {email.status === 'pending' && (
                              <Button
                                data-testid={`cancel-scheduled-${email.id}`}
                                variant="ghost"
                                size="icon"
                                onClick={() => handleCancelScheduled(email.id)}
                                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Campaign Detail Dialog */}
      <Dialog open={isCampaignDetailOpen} onOpenChange={setIsCampaignDetailOpen}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F] flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#FF6F61]" />
              Campaign Analytics
            </DialogTitle>
          </DialogHeader>
          
          {campaignLoading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" style={{ width: '32px', height: '32px' }} />
            </div>
          ) : campaignDetails ? (
            <div className="space-y-6 mt-4">
              {/* Campaign Info */}
              <div className="p-4 rounded-xl bg-[#F4F1EA]">
                <p className="font-medium text-[#0A192F]">{campaignDetails.campaign.name}</p>
                <p className="text-sm text-[#4A5568] mt-1">{campaignDetails.campaign.subject}</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-5 gap-3">
                <div className="text-center p-3 rounded-xl bg-gradient-to-br from-[#40E0D0]/10 to-[#00CED1]/10">
                  <p className="text-xl font-bold text-[#0A192F]">{campaignDetails.campaign.total_sent}</p>
                  <p className="text-xs text-[#4A5568]">Sent</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-gradient-to-br from-[#FF6F61]/10 to-[#FF7F50]/10">
                  <p className="text-xl font-bold text-[#0A192F]">{campaignDetails.campaign.total_opened}</p>
                  <p className="text-xs text-[#4A5568]">Opened</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-gradient-to-br from-[#FFB347]/10 to-[#FFD166]/10">
                  <p className="text-xl font-bold text-[#0A192F]">{campaignDetails.unique_clicks || 0}</p>
                  <p className="text-xs text-[#4A5568]">Clicked</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-gradient-to-br from-green-100 to-green-50">
                  <p className="text-xl font-bold text-[#0A192F]">{campaignDetails.open_rate}%</p>
                  <p className="text-xs text-[#4A5568]">Open Rate</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-gradient-to-br from-[#FFB347]/10 to-yellow-50">
                  <p className="text-xl font-bold text-[#0A192F]">{campaignDetails.click_rate || 0}%</p>
                  <p className="text-xs text-[#4A5568]">Click Rate</p>
                </div>
              </div>

              {/* Top Clicked Links */}
              {campaignDetails.top_links?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#4A5568] mb-3">Top Clicked Links</h4>
                  <div className="space-y-2">
                    {campaignDetails.top_links.map((link, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-[#F4F1EA]">
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#006994] hover:underline truncate max-w-[250px]"
                        >
                          {link.url}
                        </a>
                        <span className="text-xs font-medium text-[#0A192F] bg-white px-2 py-1 rounded-full">
                          {link.clicks} {link.clicks === 1 ? 'click' : 'clicks'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Opens */}
              {campaignDetails.opens?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#4A5568] mb-3">Recent Opens</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {campaignDetails.opens.slice(0, 10).map((open, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-[#F4F1EA]">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#40E0D0] to-[#00CED1] flex items-center justify-center text-white text-xs font-medium">
                            {(open.subscriber_name || open.subscriber_email || 'U').charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm text-[#0A192F] truncate max-w-[150px]">
                            {open.subscriber_name || open.subscriber_email}
                          </span>
                        </div>
                        <span className="text-xs text-[#4A5568]">
                          {format(new Date(open.opened_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {campaignDetails.opens?.length === 0 && (
                <div className="text-center py-6">
                  <Eye className="w-10 h-10 text-[#E8E2D2] mx-auto mb-2" />
                  <p className="text-[#4A5568]">No opens tracked yet</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-[#4A5568]">Failed to load campaign details</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Subscriber Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F] flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#FF6F61]" />
              Add Subscriber
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-[#4A5568] block mb-2">Name</label>
              <Input
                data-testid="subscriber-name-input"
                value={newSubscriber.name}
                onChange={(e) => setNewSubscriber({ ...newSubscriber, name: e.target.value })}
                placeholder="John Smith"
                className="input-bahamian"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#4A5568] block mb-2">Email</label>
              <Input
                data-testid="subscriber-email-input"
                type="email"
                value={newSubscriber.email}
                onChange={(e) => setNewSubscriber({ ...newSubscriber, email: e.target.value })}
                placeholder="john@example.com"
                className="input-bahamian"
              />
            </div>
            <Button
              data-testid="add-subscriber-submit"
              onClick={handleAddSubscriber}
              disabled={adding}
              className="btn-coral w-full"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Subscriber
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Email Dialog */}
      <Dialog open={isBulkEmailOpen} onOpenChange={(open) => {
        setIsBulkEmailOpen(open);
        if (!open) { setBulkScheduleMode(false); setBulkScheduleDate(''); }
      }}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F] flex items-center gap-2">
              <Send className="w-5 h-5 text-[#FF6F61]" />
              {bulkScheduleMode ? 'Schedule Bulk Email' : 'Send Bulk Email'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-[#4A5568]">
              This will {bulkScheduleMode ? 'schedule an email for' : 'send an email to'} all {subscriberCount} active subscribers.
              Use <code className="bg-[#F4F1EA] px-1 rounded">{"{{name}}"}</code> to personalize.
            </p>
            {!emailConfigured && !bulkScheduleMode && (
              <p className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                Email service not configured. Toggle "Schedule for later" to queue emails for when the key is added.
              </p>
            )}
            <div>
              <label className="text-sm font-medium text-[#4A5568] block mb-2">Subject</label>
              <Input
                data-testid="bulk-email-subject"
                value={bulkEmail.subject}
                onChange={(e) => setBulkEmail({ ...bulkEmail, subject: e.target.value })}
                placeholder="Newsletter: Big News!"
                className="input-bahamian"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#4A5568] block mb-2">Content (HTML)</label>
              <Textarea
                data-testid="bulk-email-content"
                value={bulkEmail.html_content}
                onChange={(e) => setBulkEmail({ ...bulkEmail, html_content: e.target.value })}
                placeholder="<p>Hello {{name}},</p><p>We have exciting news...</p>"
                className="input-bahamian min-h-[150px] font-mono text-sm"
              />
            </div>

            {/* Schedule Toggle */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F4F1EA]">
              <button
                data-testid="bulk-schedule-toggle"
                type="button"
                onClick={() => setBulkScheduleMode(!bulkScheduleMode)}
                className={`relative w-11 h-6 rounded-full transition-colors ${bulkScheduleMode ? 'bg-[#40E0D0]' : 'bg-gray-300'}`}
              >
                <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform absolute top-1 ${bulkScheduleMode ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-[#4A5568]">Schedule for later</span>
            </div>

            {bulkScheduleMode && (
              <div>
                <label className="text-sm font-medium text-[#4A5568] block mb-2">Send Date & Time</label>
                <Input
                  data-testid="bulk-schedule-datetime"
                  type="datetime-local"
                  value={bulkScheduleDate}
                  onChange={(e) => setBulkScheduleDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="input-bahamian"
                />
              </div>
            )}

            <Button
              data-testid="send-bulk-email-btn"
              onClick={handleSendBulkEmail}
              disabled={sending || (!bulkScheduleMode && !emailConfigured)}
              className="btn-coral w-full"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {bulkScheduleMode ? 'Scheduling...' : 'Sending...'}</>
              ) : bulkScheduleMode ? (
                <><CalendarClock className="w-4 h-4 mr-2" />Schedule Email</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Send to {subscriberCount} Subscribers</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notify Publication Dialog */}
      <Dialog open={isNotifyOpen} onOpenChange={(open) => {
        setIsNotifyOpen(open);
        if (!open) { setNotifyScheduleMode(false); setNotifyScheduleDate(''); }
      }}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F] flex items-center gap-2">
              <MailOpen className="w-5 h-5 text-[#FF6F61]" />
              {notifyScheduleMode ? 'Schedule Publication Notification' : 'Notify New Publication'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-[#4A5568]">
              {notifyScheduleMode 
                ? 'Schedule a notification email to all subscribers about a published magazine.'
                : 'Send a beautifully designed notification email to all subscribers about a published magazine.'}
            </p>
            <div>
              <label className="text-sm font-medium text-[#4A5568] block mb-2">Select Magazine</label>
              <Select value={selectedMagazine} onValueChange={setSelectedMagazine}>
                <SelectTrigger data-testid="magazine-select" className="input-bahamian">
                  <SelectValue placeholder="Choose a published magazine" />
                </SelectTrigger>
                <SelectContent>
                  {magazines.map((mag) => (
                    <SelectItem key={mag.id} value={mag.id}>
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-[#4A5568]" />
                        {mag.title}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {magazines.length === 0 && (
              <p className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg">
                No published magazines available. Publish a magazine first to send notifications.
              </p>
            )}

            {/* Schedule Toggle */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F4F1EA]">
              <button
                data-testid="notify-schedule-toggle"
                type="button"
                onClick={() => setNotifyScheduleMode(!notifyScheduleMode)}
                className={`relative w-11 h-6 rounded-full transition-colors ${notifyScheduleMode ? 'bg-[#40E0D0]' : 'bg-gray-300'}`}
              >
                <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform absolute top-1 ${notifyScheduleMode ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-[#4A5568]">Schedule for later</span>
            </div>

            {notifyScheduleMode && (
              <div>
                <label className="text-sm font-medium text-[#4A5568] block mb-2">Send Date & Time</label>
                <Input
                  data-testid="notify-schedule-datetime"
                  type="datetime-local"
                  value={notifyScheduleDate}
                  onChange={(e) => setNotifyScheduleDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="input-bahamian"
                />
              </div>
            )}

            <Button
              data-testid="send-notification-btn"
              onClick={handleNotifyPublication}
              disabled={notifying || !selectedMagazine || (!notifyScheduleMode && !emailConfigured)}
              className="btn-coral w-full"
            >
              {notifying ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {notifyScheduleMode ? 'Scheduling...' : 'Sending...'}</>
              ) : notifyScheduleMode ? (
                <><CalendarClock className="w-4 h-4 mr-2" />Schedule Notification</>
              ) : (
                <><Mail className="w-4 h-4 mr-2" />Send Notification</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={isBulkImportOpen} onOpenChange={setIsBulkImportOpen}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F] flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#40E0D0]" />
              Bulk Import Subscribers
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-[#4A5568]">
              Paste subscriber data below — one per line. Supported formats:
            </p>
            <div className="bg-[#F4F1EA] rounded-lg p-3 text-xs text-[#4A5568] font-mono space-y-1">
              <p>email@example.com</p>
              <p>email@example.com, John Doe</p>
              <p>John Doe, email@example.com</p>
              <p>John Doe &lt;email@example.com&gt;</p>
            </div>
            <Textarea
              data-testid="bulk-import-textarea"
              value={bulkImportText}
              onChange={(e) => setBulkImportText(e.target.value)}
              placeholder={"john@example.com, John Doe\njane@example.com, Jane Smith\nbob@example.com"}
              className="input-bahamian min-h-[200px] font-mono text-sm"
            />
            <div className="flex items-center justify-between text-xs text-[#4A5568]">
              <span>
                {bulkImportText.trim() ? bulkImportText.split('\n').filter(l => l.trim()).length : 0} entries detected
              </span>
              <label className="flex items-center gap-2 cursor-pointer hover:text-[#0A192F] transition-colors">
                <FileText className="w-3.5 h-3.5" />
                <span>Upload CSV</span>
                <input
                  data-testid="bulk-import-file"
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const text = ev.target?.result;
                      if (text) setBulkImportText(prev => prev ? prev + '\n' + text : text);
                    };
                    reader.readAsText(file);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            <Button
              data-testid="bulk-import-submit"
              onClick={handleBulkImport}
              disabled={bulkImporting || !bulkImportText.trim()}
              className="btn-coral w-full"
            >
              {bulkImporting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />Importing...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" />Import Subscribers</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <CenturionFooter />
    </div>
  );
};

export default SubscribersPage;
